import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useI18n } from '../../../../contexts/I18nContext';
import './ParameterSelector.scss';

const ParameterSelector = ({
  id,
  title,
  description,
  required = false,
  value,
  onChange,
  onValidationChange,
  disabled = false,
  className = '',
  props = {}
}) => {
  const { t } = useI18n();
  const [isValid, setIsValid] = useState(true);
  const [validationMessage, setValidationMessage] = useState('');
  const validationCallbackRef = useRef(onValidationChange);
  // 从props中提取具体属性
  const {
    options = [],
    displayType = 'cards', // 'cards', 'dropdown', 'buttons'
    allowMultiple = false,
    placeholder = t('aiToolTemplate.parameterSelector.placeholder') || 'Select an option...'
  } = props;

  // 验证函数
  const validateValue = useCallback((val) => {
    let valid = true;
    let message = '';

    if (required && (!val || (Array.isArray(val) && val.length === 0))) {
      valid = false;
      message = t('aiToolTemplate.parameterSelector.validation.required') || 'Please select an option';
    }

    setIsValid(valid);
    setValidationMessage(message);

    if (validationCallbackRef.current) {
      validationCallbackRef.current(valid, message);
    }

    return valid;
  }, [required, t]);

  useEffect(() => {
    validationCallbackRef.current = onValidationChange;
  }, [onValidationChange]);

  // 处理选项选择
  const handleOptionSelect = (optionValue) => {
    if (disabled) return;

    let newValue;
    if (allowMultiple) {
      const currentValues = Array.isArray(value) ? value : [];
      if (currentValues.includes(optionValue)) {
        newValue = currentValues.filter(v => v !== optionValue);
      } else {
        newValue = [...currentValues, optionValue];
      }
    } else {
      newValue = optionValue;
    }

    validateValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  // 检查选项是否被选中
  const isOptionSelected = (optionValue) => {
    if (allowMultiple) {
      return Array.isArray(value) && value.includes(optionValue);
    }
    return value === optionValue;
  };

  // 渲染卡片式选择器
  const renderCards = () => (
    <div className="parameter-cards">
      {options.map((option, index) => (
        <div
          key={option.value || index}
          className={`parameter-card ${isOptionSelected(option.value) ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
          onClick={() => handleOptionSelect(option.value)}
        >
          {option.icon && <div className="card-icon">{option.icon}</div>}
          <div className="card-content">
            <div className="card-label">{option.label}</div>
            {option.description && (
              <div className="card-description">{option.description}</div>
            )}
          </div>
          {isOptionSelected(option.value) && (
            <div className="selection-indicator">✓</div>
          )}
        </div>
      ))}
    </div>
  );

  // 渲染按钮式选择器
  const renderButtons = () => (
    <div className="parameter-buttons">
      {options.map((option, index) => (
        <button
          key={option.value || index}
          type="button"
          className={`parameter-button ${isOptionSelected(option.value) ? 'selected' : ''}`}
          onClick={() => handleOptionSelect(option.value)}
          disabled={disabled}
          title={option.description}
        >
          {option.icon && <span className="button-icon">{option.icon}</span>}
          <span className="button-label">{option.label}</span>
        </button>
      ))}
    </div>
  );

  // 渲染下拉式选择器
  const renderDropdown = () => (
    <div className="parameter-dropdown">
      <select
        value={value || ''}
        onChange={(e) => handleOptionSelect(e.target.value)}
        disabled={disabled}
        className={`dropdown-select ${!isValid ? 'invalid' : ''}`}
      >
        <option value="">{placeholder}</option>
        {options.map((option, index) => (
          <option key={option.value || index} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );

  // 根据显示类型渲染对应的组件
  const renderSelector = () => {
    switch (displayType) {
      case 'buttons':
        return renderButtons();
      case 'dropdown':
        return renderDropdown();
      case 'cards':
      default:
        return renderCards();
    }
  };

  return (
    <div className={`parameter-selector-container ${className} ${!isValid ? 'invalid' : ''}`}>
      {/* 标题和描述 */}
      <div className="component-header">
        <h3 className="component-title">
          {title}
          {required && <span className="required-indicator">*</span>}
        </h3>
        {description && (
          <p className="component-description">{description}</p>
        )}
      </div>

      {/* 参数选择器 */}
      <div className="parameter-selector-content">
        {renderSelector()}
      </div>

      {/* 验证错误信息 */}
      {!isValid && validationMessage && (
        <div className="validation-message error">
          {validationMessage}
        </div>
      )}
    </div>
  );
};

export default ParameterSelector;
